import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { CurrencyInput } from '@/components/ui/currency-input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useToast } from '@/hooks/use-toast'
import { Loader2, Plus } from 'lucide-react'

interface AddAdjustmentDialogProps {
  bookingId: string
  currentTotal: number
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess?: () => void
}

// Discounts are handled by ApplyDiscountDialog (percent or flat, one per booking),
// so this dialog covers only the additive adjustments.
type AdjustmentType = 'fee' | 'extra_hours'

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount)

export function AddAdjustmentDialog({
  bookingId,
  currentTotal,
  open,
  onOpenChange,
  onSuccess,
}: AddAdjustmentDialogProps) {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const { profile } = useAuth()

  const [adjustmentType, setAdjustmentType] = useState<AdjustmentType>('fee')
  const [label, setLabel] = useState('')
  const [amount, setAmount] = useState<number | null>(null)
  const [hours, setHours] = useState('')
  const [hourlyRate, setHourlyRate] = useState<number | null>(null)
  const [notes, setNotes] = useState('')

  // Calculate the adjustment amount
  const calculatedAmount = (() => {
    if (adjustmentType === 'extra_hours') {
      const h = parseFloat(hours) || 0
      const rate = hourlyRate || 0
      return h * rate
    }
    return amount || 0
  })()

  const newTotal = currentTotal + calculatedAmount

  const resetForm = () => {
    setAdjustmentType('fee')
    setLabel('')
    setAmount(null)
    setHours('')
    setHourlyRate(null)
    setNotes('')
  }

  const addAdjustmentMutation = useMutation({
    mutationFn: async () => {
      // Generate label if not provided
      const adjustmentLabel =
        label ||
        (adjustmentType === 'extra_hours' ? `Extra ${hours} hour(s)` : 'Additional Fee')

      // Insert the adjustment
      const { error: adjustmentError } = await supabase
        .from('booking_adjustments')
        .insert({
          booking_id: bookingId,
          adjustment_type: adjustmentType,
          label: adjustmentLabel,
          amount: Math.abs(calculatedAmount),
          quantity: adjustmentType === 'extra_hours' ? parseFloat(hours) || 1 : 1,
          notes: notes || null,
          created_by: profile?.id || null,
        })

      if (adjustmentError) throw adjustmentError

      // Note: We do NOT update booking.total_amount here.
      // The original total should remain immutable - adjustments are tracked
      // separately in booking_adjustments and the adjusted total is calculated dynamically.
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['booking-detail', bookingId] })
      queryClient.invalidateQueries({ queryKey: ['booking-adjustments', bookingId] })
      toast({
        title: 'Adjustment added',
        description: `Charge of ${formatCurrency(calculatedAmount)} has been added.`,
      })
      resetForm()
      onOpenChange(false)
      onSuccess?.()
    },
    onError: (error) => {
      toast({
        title: 'Error adding adjustment',
        description: error.message,
        variant: 'destructive',
      })
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (calculatedAmount <= 0) {
      toast({
        title: 'Invalid amount',
        description: 'Please enter a valid amount greater than 0.',
        variant: 'destructive',
      })
      return
    }

    addAdjustmentMutation.mutate()
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Add Adjustment</DialogTitle>
            <DialogDescription>
              Add a fee or extra hours to this booking. Use Apply Discount to reduce
              the total.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            {/* Adjustment Type */}
            <div className="grid gap-2">
              <Label htmlFor="type">Type</Label>
              <Select
                value={adjustmentType}
                onValueChange={(v) => setAdjustmentType(v as AdjustmentType)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="fee">
                    <div className="flex items-center gap-2">
                      <Plus className="h-3 w-3" />
                      Additional Fee
                    </div>
                  </SelectItem>
                  <SelectItem value="extra_hours">
                    <div className="flex items-center gap-2">
                      <Plus className="h-3 w-3" />
                      Extra Hours
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Label */}
            <div className="grid gap-2">
              <Label htmlFor="label">Description</Label>
              <Input
                id="label"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder={
                  adjustmentType === 'fee'
                    ? 'e.g., Wait time, Cleaning fee'
                    : 'e.g., Extended trip'
                }
              />
            </div>

            {/* Amount fields based on type */}
            {adjustmentType === 'extra_hours' ? (
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="hours">Hours</Label>
                  <Input
                    id="hours"
                    type="number"
                    step="0.5"
                    min="0.5"
                    value={hours}
                    onChange={(e) => setHours(e.target.value)}
                    placeholder="1"
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="rate">Hourly Rate</Label>
                  <CurrencyInput
                    value={hourlyRate}
                    onChange={setHourlyRate}
                    placeholder="75.00"
                  />
                </div>
              </div>
            ) : (
              <div className="grid gap-2">
                <Label htmlFor="amount">Amount</Label>
                <CurrencyInput
                  value={amount}
                  onChange={setAmount}
                  placeholder="25.00"
                />
              </div>
            )}

            {/* Notes */}
            <div className="grid gap-2">
              <Label htmlFor="notes">Notes (optional)</Label>
              <Textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Internal notes about this adjustment..."
                rows={2}
              />
            </div>

            {/* Preview */}
            {calculatedAmount > 0 && (
              <div className="p-3 bg-muted rounded-lg space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Current Adjusted Total</span>
                  <span>{formatCurrency(currentTotal)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Adjustment</span>
                  <span>+{formatCurrency(calculatedAmount)}</span>
                </div>
                <div className="flex justify-between font-medium border-t pt-2">
                  <span>New Adjusted Total</span>
                  <span>{formatCurrency(newTotal)}</span>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={addAdjustmentMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={addAdjustmentMutation.isPending || calculatedAmount <= 0}
            >
              {addAdjustmentMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Adding...
                </>
              ) : (
                'Add Adjustment'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
